import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { AnalyticsEvents } from '@app/core/enums/analytics-events.enum';
import { ProcessNames } from '@app/core/enums/process.enum';
import { ToastSandbox } from '@app/core/sandboxes/toast.sandbox';
import { AnalyticsService } from '@app/core/services/analytics.service';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

interface FeedbackItem {
  src: string;
  description: string;
  code: number;
  color: string;
  loading: boolean;
}

const SRC_ROOT = 'assets/img/feedback/rate-week/';

@Component({
    selector: 'c-rate-week-modal',
    templateUrl: './rate-week-modal.component.html',
    styleUrls: ['./rate-week-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RateWeekModalComponent implements OnInit {
  private readonly toastSandbox = inject(ToastSandbox);
  private readonly analyticsService = inject(AnalyticsService);

  close: () => void;
  submit: (code: number) => void;
  submitting$: Observable<boolean>;
  submitted$: Observable<boolean>;

  selectedOption$ = new BehaviorSubject<FeedbackItem>(null);
  items: FeedbackItem[] = [
    { src: `${SRC_ROOT}great.svg`, description: 'rate-week.great', code: 2, color: 'green', loading: false },
    { src: `${SRC_ROOT}okay.svg`, description: 'rate-week.okay', code: 3, color: 'yellow', loading: false },
    { src: `${SRC_ROOT}not-great.svg`, description: 'rate-week.not-great', code: 4, color: 'red', loading: false },
    { src: `${SRC_ROOT}no-sharing.svg`, description: 'rate-week.no-sharing', code: 1, color: 'gray', loading: false },
  ];

  ngOnInit(): void {
    this.analyticsService.logEvent(AnalyticsEvents.Process, {
      name: ProcessNames.RateYourWeek,
    });
  }

  async sendFeedback(item: FeedbackItem): Promise<void> {
    const loading = await firstValueFrom(this.submitting$);
    if (loading) {
      return;
    }

    try {
      item.loading = true;
      this.submit(item.code);
      await firstValueFrom(this.submitted$);
      this.selectedOption$.next(item);
      this.analyticsService.logEvent(AnalyticsEvents.ProcessCompleted, {
        name: ProcessNames.RateYourWeek,
      });
    } catch {
      this.toastSandbox.showErrorToast({key: 'rate-week.error'});
      this.close();
    } finally {
      item.loading = false;
    }
  }
}
